/**
 * The emoji catalogue, generated from Unicode's emoji blocks.
 *
 * Generated from codepoint ranges rather than shipped as a literal list, and
 * rather than pulled from a package: emoji-mart and emojibase are hundreds of
 * kilobytes of names, keywords and skin-tone data, which is a lot of bundle for
 * a support composer. Ranges give the whole set for a few hundred bytes of
 * source.
 *
 * The ranges below are the assigned, widely-supported parts of each block.
 * They are deliberately trimmed at the ends of the newer blocks, where recent
 * additions are unassigned on older systems and would render as tofu.
 *
 * Search matches the keyword index at the bottom, which covers the emoji people
 * actually reach for by name. Everything else is browsable by category, which
 * is how a picker is used in practice anyway.
 */

export interface EmojiGroup {
  id: string;
  label: string;
  /** Single glyph shown on the category tab. */
  icon: string;
  emoji: string[];
}

type Range = [start: number, end: number];

function expand(ranges: Range[]): string[] {
  const out: string[] = [];
  for (const [start, end] of ranges) {
    for (let code = start; code <= end; code += 1) {
      out.push(String.fromCodePoint(code));
    }
  }
  return out;
}

export const EMOJI_GROUPS: EmojiGroup[] = [
  {
    id: "smileys",
    label: "Smileys & emotion",
    icon: "😀",
    emoji: expand([
      [0x1f600, 0x1f64f], // Emoticons
      [0x1f910, 0x1f92f], // Additional faces
      [0x1f970, 0x1f97a],
      [0x1f9d0, 0x1f9d0],
      [0x2764, 0x2764], // Heart
      [0x1f493, 0x1f49f], // Hearts
    ]),
  },
  {
    id: "people",
    label: "People & body",
    icon: "👍",
    emoji: expand([
      [0x1f440, 0x1f450], // Eyes, hands
      [0x1f464, 0x1f487],
      [0x1f9b0, 0x1f9b9],
      [0x1f9d1, 0x1f9dd],
      [0x1faf0, 0x1faf6], // Newer hands
    ]),
  },
  {
    id: "nature",
    label: "Animals & nature",
    icon: "🐢",
    emoji: expand([
      [0x1f400, 0x1f43f],
      [0x1f330, 0x1f343],
      [0x1f980, 0x1f9ae],
      [0x1f990, 0x1f99f],
    ]),
  },
  {
    id: "food",
    label: "Food & drink",
    icon: "🍎",
    emoji: expand([
      [0x1f344, 0x1f37f],
      [0x1f950, 0x1f96f],
      [0x1f9c0, 0x1f9cb],
    ]),
  },
  {
    id: "travel",
    label: "Travel & places",
    icon: "✈️",
    emoji: expand([
      [0x1f680, 0x1f6a4],
      [0x1f6b2, 0x1f6bf],
      [0x1f3d4, 0x1f3f0],
      [0x1f30d, 0x1f320],
    ]),
  },
  {
    id: "activities",
    label: "Activities",
    icon: "⚽",
    emoji: expand([
      [0x1f3a0, 0x1f3ca],
      [0x1f380, 0x1f397],
      [0x1f947, 0x1f94c],
    ]),
  },
  {
    id: "objects",
    label: "Objects",
    icon: "💡",
    emoji: expand([
      [0x1f4a1, 0x1f4ff],
      [0x1f5a4, 0x1f5a4],
      [0x1f9f0, 0x1f9ff],
      [0x1f4b0, 0x1f4b9],
    ]),
  },
  {
    id: "symbols",
    label: "Symbols",
    icon: "✅",
    emoji: expand([
      [0x2600, 0x26ff], // Misc symbols
      [0x2700, 0x27bf], // Dingbats
      [0x1f500, 0x1f53d],
      [0x1f4ac, 0x1f4ad],
    ]),
  },
];

/**
 * Keyword index for search.
 *
 * Covers the emoji a support desk reaches for by name. Anything absent is still
 * browsable by category -- this exists so typing "tick" or "warning" lands
 * immediately, not to be an exhaustive Unicode name table.
 */
export const EMOJI_KEYWORDS: Record<string, string> = {
  "😀": "grin smile happy",
  "😃": "smile happy",
  "😄": "smile happy laugh",
  "😅": "sweat laugh nervous",
  "😊": "smile blush happy",
  "🙂": "smile slight",
  "😉": "wink",
  "😍": "love heart eyes",
  "🤔": "think hmm consider",
  "😌": "relieved calm",
  "😐": "neutral",
  "😕": "confused",
  "🙁": "sad frown",
  "😞": "sad disappointed",
  "😬": "grimace awkward",
  "😢": "cry sad",
  "😭": "cry sob",
  "😡": "angry",
  "🥳": "party celebrate",
  "😎": "cool sunglasses",
  "🤝": "handshake deal agree",
  "👍": "thumbs up yes good approve",
  "👎": "thumbs down no bad",
  "👋": "wave hello hi bye",
  "🙏": "thanks please pray",
  "👏": "clap applause well done",
  "💪": "strong muscle",
  "👀": "eyes look watch",
  "🎉": "party celebrate tada",
  "✅": "tick check done yes complete",
  "❌": "cross no wrong fail",
  "⚠️": "warning caution careful",
  "❗": "exclamation important",
  "❓": "question",
  "💡": "idea lightbulb tip",
  "🔥": "fire hot urgent",
  "⭐": "star favourite",
  "❤️": "heart love",
  "📦": "box parcel package delivery shipping",
  "🚚": "truck delivery shipping courier",
  "📧": "email mail message",
  "📨": "email incoming mail",
  "📞": "phone call",
  "📱": "mobile phone",
  "💻": "laptop computer",
  "🖥️": "desktop computer monitor",
  "🔌": "plug power",
  "🔧": "spanner wrench tool fix repair",
  "🔨": "hammer fix",
  "🧾": "receipt invoice",
  "💳": "card payment credit",
  "💰": "money cash payment",
  "📅": "calendar date",
  "🕐": "clock time",
  "⏰": "alarm clock time reminder",
  "🔗": "link url",
  "📎": "paperclip attachment",
  "📝": "note write memo",
  "📄": "document page file",
  "🔒": "lock secure private",
  "🔑": "key access",
  "🚀": "rocket launch fast",
  "🎯": "target goal",
  "♻️": "recycle return",
  "↩️": "return reply back",
};
